export const WORKER_PROTOCOL = 'saas-worker-v1' as const

export type WorkerProgressUnit = 'bytes' | 'items' | 'percent'
export type WorkerErrorKind = 'recoverable' | 'fatal'

export interface WorkerProgress {
  completed: number
  total?: number
  unit: WorkerProgressUnit
  stage?: string
}

export interface WorkerStartMessage<Input> {
  protocol: typeof WORKER_PROTOCOL
  type: 'start'
  jobId: string
  input: Input
}

export interface WorkerProgressMessage {
  protocol: typeof WORKER_PROTOCOL
  type: 'progress'
  jobId: string
  progress: WorkerProgress
}

export interface WorkerResultMessage<Result> {
  protocol: typeof WORKER_PROTOCOL
  type: 'result'
  jobId: string
  result: Result
}

export interface WorkerErrorPayload {
  kind: WorkerErrorKind
  code: string
}

export interface WorkerErrorMessage {
  protocol: typeof WORKER_PROTOCOL
  type: 'error'
  jobId: string
  error: WorkerErrorPayload
}

export type WorkerEventMessage<Result> =
  | WorkerProgressMessage
  | WorkerResultMessage<Result>
  | WorkerErrorMessage

export class WorkerJobError extends Error {
  readonly kind: WorkerErrorKind
  readonly code: string

  constructor(kind: WorkerErrorKind, code: string) {
    super(code)
    this.name = 'WorkerJobError'
    this.kind = kind
    this.code = code
  }
}

export function createWorkerError(kind: WorkerErrorKind, code: string): WorkerJobError {
  return new WorkerJobError(kind, code)
}

export function isWorkerJobError(value: unknown): value is WorkerJobError {
  return value instanceof WorkerJobError
}

export function createAbortError(): DOMException {
  return new DOMException('The worker job was cancelled.', 'AbortError')
}

export function isAbortError(value: unknown): boolean {
  return value instanceof DOMException && value.name === 'AbortError'
}
